import React, { useEffect, useRef, useState } from "react";
import "../../styles/common/popup.scss";
import "../../styles/admin/questionBank.scss";
import "../../styles/common/contextPopup.scss";
import { ArrowBackIcon, FileIcon } from "../../icons";
import GlobalLoader from "./GlobalLoader";
import MarkdownViewer from "./MarkdownViewer";
import TooltipText from "./tooltipText";
import pdfIcon from "../../assets/svg/pdfIcon.svg";
import playIcon from "../../assets/png/playIcon.png";
import audioIcon from "../../assets/png/audioIcon.png";
import imgIcon from "../../assets/png/imgIcon.png";
import powerpointIcon from "../../assets/png/powerpointIcon.png";
import { convertPdfToMarkdown } from "../../helper/apicalls/materials";
import { getMaterialById } from "../../helper/apicalls/materials";
import { GlobalWorkerOptions } from "pdfjs-dist";
import pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import pdfWorker from "pdfjs-dist/legacy/build/pdf.worker.entry";

GlobalWorkerOptions.workerSrc = pdfWorker;

const ContextPopup = ({
  open,
  onClose,
  materials = [],
  loading = false,
  error = "",
  onExtract,
  pageSelections = {},
}) => {
  const conversionSocketRef = useRef(null);
  const conversionCancelledRef = useRef(false);
  const pagesSpecManuallyEditedRef = useRef(false);

  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [pagesInput, setPagesInput] = useState("");
  const [pagesError, setPagesError] = useState("");
  const [numPages, setNumPages] = useState(0);
  const [contextPagesSpecDraft, setContextPagesSpecDraft] = useState("");
  const [contextPagesSpec, setContextPagesSpec] = useState("");
  const [appliedPagesText, setAppliedPagesText] = useState("");
  const [markdown, setMarkdown] = useState("");
  const [markdownLoading, setMarkdownLoading] = useState(false);
  const [markdownError, setMarkdownError] = useState("");
  const [showMarkdown, setShowMarkdown] = useState(false);
  const [markdownTotalPages, setMarkdownTotalPages] = useState(0);
  const [markdownCurrentPage, setMarkdownCurrentPage] = useState(0);
  const [isMarkdownConverted, setIsMarkdownConverted] = useState(false);
  const [generatedMarkdownPages, setGeneratedMarkdownPages] = useState({});
  const [selectedGeneratedPages, setSelectedGeneratedPages] = useState({});
  const [materialFetchLoading, setMaterialFetchLoading] = useState(false);
  const [materialFetchError, setMaterialFetchError] = useState("");

  const getMaxPages = () => {
    if (Number(markdownTotalPages) > 0) return Number(markdownTotalPages);
    if (Number(numPages) > 0) return Number(numPages);
    return 0;
  };

  const validatePagesSpec = (spec) => {
    const raw = String(spec || "");
    const cleaned = raw.replace(/\s+/g, "");
    if (!cleaned) return "";

    if (!/^[0-9,\-]*$/.test(cleaned)) {
      return "Only numbers, comma (,) and hyphen (-) are allowed.";
    }

    const parts = cleaned.split(",");
    if (parts.some((p) => p === "")) {
      return "Invalid pages format.";
    }

    for (const part of parts) {
      if (part.includes("-")) {
        if (!/^\d+-\d+$/.test(part)) return "Invalid range format.";
        const [startStr, endStr] = part.split("-");
        const start = Number(startStr);
        const end = Number(endStr);
        if (!Number.isFinite(start) || !Number.isFinite(end)) {
          return "Invalid range format.";
        }
        if (start < 1 || end < 1) {
          return "Page numbers should start from 1.";
        }
        if (start > end) {
          return "Range start page must be less than or equal to end page.";
        }
      } else {
        if (!/^\d+$/.test(part)) return "Invalid page number format.";
        const n = Number(part);
        if (!Number.isFinite(n)) return "Invalid page number format.";
        if (n < 1) return "Page numbers should start from 1.";
      }
    }

    const maxPages = getMaxPages();
    if (maxPages > 0) {
      const nums = cleaned.match(/\d+/g) || [];
      const tooLarge = nums.some((m) => {
        const n = Number(m);
        return Number.isFinite(n) && n > maxPages;
      });
      if (tooLarge)
        return `Please enter page numbers between 1 and ${maxPages}.`;
    }

    return "";
  };

  useEffect(() => {
    if (open) {
    } else {
      conversionCancelledRef.current = true;

      setSelectedMaterial(null);
      setPagesInput("");
      setPagesError("");
      setNumPages(0);
      setContextPagesSpecDraft("");
      setContextPagesSpec("");
      setAppliedPagesText("");
      pagesSpecManuallyEditedRef.current = false;
      setMarkdown("");
      setMarkdownError("");
      setMarkdownLoading(false);
      setShowMarkdown(false);
      setMarkdownTotalPages(0);
      setMarkdownCurrentPage(0);
      setIsMarkdownConverted(false);
      setGeneratedMarkdownPages({});
      setSelectedGeneratedPages({});
    }
  }, [open]);

  useEffect(() => {
    return () => {
      conversionCancelledRef.current = true;
    };
  }, []);

  useEffect(() => {
    setMarkdown("");
    setMarkdownError("");
    setMarkdownLoading(false);
    setShowMarkdown(false);
    setMarkdownTotalPages(0);
    setMarkdownCurrentPage(0);
    setIsMarkdownConverted(false);
    setGeneratedMarkdownPages({});
    setSelectedGeneratedPages({});
    setMaterialFetchLoading(false);
    setMaterialFetchError("");
    setContextPagesSpecDraft("");
    setContextPagesSpec("");
    setAppliedPagesText("");
    pagesSpecManuallyEditedRef.current = false;
  }, [selectedMaterial]);

  const normalizeMaterialResponse = (data) => {
    if (!data) return null;
    if (data?.data) return data.data;
    return data;
  };

  const handleSelectMaterial = async (m) => {
    setMaterialFetchError("");
    setMaterialFetchLoading(true);
    setSelectedMaterial(m);
    setPagesInput("");

    const id = m?.id || m?._id;
    if (!id) {
      setMaterialFetchLoading(false);
      return;
    }

    try {
      const full = await getMaterialById(id);
      setSelectedMaterial(normalizeMaterialResponse(full) || m);
    } catch (err) {
      console.error("Failed to load material by id", err);
      setMaterialFetchError("Unable to load this material.");
    } finally {
      setMaterialFetchLoading(false);
    }
  };

  useEffect(() => {
    if (String(contextPagesSpec).trim()) return;

    const selectedPages = Object.keys(selectedGeneratedPages)
      .filter((k) => selectedGeneratedPages[k])
      .map((k) => Number(k))
      .filter((n) => Number.isFinite(n))
      .sort((a, b) => a - b);

    if (selectedPages.length === 0) {
      setPagesInput("");
      setAppliedPagesText("");
      if (!pagesSpecManuallyEditedRef.current) {
        setContextPagesSpecDraft("");
      }
      return;
    }

    if (!pagesSpecManuallyEditedRef.current) {
      setContextPagesSpecDraft(selectedPages.join(","));
    }

    setAppliedPagesText(selectedPages.join(", "));

    const combined = selectedPages
      .map((p) => {
        const pageMd = generatedMarkdownPages[p] || "";
        if (!pageMd) return "";
        return `### Page ${p}\n\n${pageMd}`;
      })
      .filter(Boolean)
      .join("\n\n");

    setPagesInput(combined);
  }, [generatedMarkdownPages, selectedGeneratedPages, contextPagesSpec]);

  useEffect(() => {
    const spec = String(contextPagesSpec || "").trim();
    if (!spec) return;

    const maxPages =
      Number(markdownTotalPages) > 0
        ? Number(markdownTotalPages)
        : Number(numPages) > 0
          ? Number(numPages)
          : 0;

    const clamp = (n) => {
      if (!Number.isFinite(n)) return null;
      if (n <= 0) return null;
      if (maxPages > 0) return Math.min(Math.max(1, n), maxPages);
      return n;
    };

    const rawPages = extractPageNumbers(spec);
    const clampedPages = Array.from(
      new Set(rawPages.map((p) => clamp(p)).filter((p) => Number.isFinite(p))),
    ).sort((a, b) => a - b);

    if (clampedPages.length === 0) {
      setPagesInput("");
      setSelectedGeneratedPages({});
      setAppliedPagesText("");
      return;
    }

    const combined = clampedPages
      .map((p) => {
        const pageMd = generatedMarkdownPages[p] || "";
        if (!pageMd) return "";
        return `### Page ${p}\n\n${pageMd}`;
      })
      .filter(Boolean)
      .join("\n\n");

    setPagesInput(combined);
    setAppliedPagesText(clampedPages.join(", "));
    setSelectedGeneratedPages(() => {
      const next = {};
      clampedPages.forEach((p) => {
        next[p] = true;
      });
      return next;
    });
  }, [contextPagesSpec, generatedMarkdownPages, markdownTotalPages, numPages]);

  useEffect(() => {
    const loadPdfPageCount = async () => {
      setPagesError("");
      setNumPages(0);

      if (!selectedMaterial) return;
      if (
        selectedMaterial.materials_type !== "pdf" ||
        !selectedMaterial.content
      ) {
        return;
      }

      const materialId = selectedMaterial.id || selectedMaterial._id;
      if (pageSelections[materialId]) {
        setPagesInput(pageSelections[materialId]);
      } else {
      }

      try {
        const pdfBuffer = await fetchPdfAsArrayBuffer(selectedMaterial.content);
        const pdf = await pdfjsLib.getDocument({ data: pdfBuffer }).promise;
        const total = pdf.numPages || 0;
        setNumPages(total);
      } catch (e) {
        console.error("Failed to load PDF for page count", e);
      }
    };

    loadPdfPageCount();
  }, [selectedMaterial]);

  const handleConvertToMarkdown = async () => {
    if (!selectedMaterial || selectedMaterial.materials_type !== "pdf") return;
    if (!selectedMaterial.content) return;

    try {
      conversionCancelledRef.current = false;
      try {
        if (conversionSocketRef.current) conversionSocketRef.current.close();
      } catch (_) {}
      conversionSocketRef.current = null;

      setMarkdown("");
      setMarkdownError("");
      setMarkdownLoading(true);
      setShowMarkdown(false);
      setMarkdownTotalPages(0);
      setMarkdownCurrentPage(0);
      setIsMarkdownConverted(false);

      const response = await fetch(selectedMaterial.content, { mode: "cors" });
      if (!response.ok) {
        throw new Error("Failed to download PDF for conversion");
      }

      const blob = await response.blob();
      const file = new File([blob], "material.pdf", {
        type: blob.type || "application/pdf",
      });

      const materialId = selectedMaterial?.id || selectedMaterial?._id;

      const pages = new Map();
      const result = await convertPdfToMarkdown(file, {
        materialId,
        onSocket: (ws) => {
          conversionSocketRef.current = ws;
        },
        isCancelled: () => conversionCancelledRef.current,
        onProgress: (progress) => {
          if (progress?.status === "started") {
            setMarkdownTotalPages(Number(progress?.total_pages) || 0);
            setMarkdownCurrentPage(0);
            setShowMarkdown(true);
            return;
          }

          if (progress?.status === "processing") {
            const p = Number(progress?.page) || 0;
            if (p > 0 && typeof progress?.markdown === "string") {
              pages.set(p, progress.markdown);
              setGeneratedMarkdownPages((prev) => ({
                ...prev,
                [p]: progress.markdown,
              }));
              const combined = Array.from(pages.entries())
                .sort((a, b) => a[0] - b[0])
                .map(([pageNo, md]) => `### Page ${pageNo}\n\n${md}`)
                .filter(Boolean)
                .join("\n\n");
              setMarkdownCurrentPage(p);
              setMarkdownTotalPages(Number(progress?.total_pages) || 0);
              setMarkdown(combined);
              setShowMarkdown(true);
            }
            return;
          }

          if (progress?.status === "completed") {
            const total = Number(progress?.total_pages) || 0;
            setMarkdownTotalPages(total);
            if (total > 0) {
              setMarkdownCurrentPage(total);
            }
            setIsMarkdownConverted(true);
          }
        },
      });
      const md =
        (result &&
          (result?.markdown ||
            result?.markdown?.content ||
            result.md ||
            result.content ||
            (result.data &&
              (result.data.markdown ||
                result.data.md ||
                result.data.content)))) ||
        "";

      const finalMd =
        pages.size > 0
          ? Array.from(pages.entries())
              .sort((a, b) => a[0] - b[0])
              .map(([pageNo, pageMd]) => `### Page ${pageNo}\n\n${pageMd}`)
              .filter(Boolean)
              .join("\n\n")
          : md;

      setMarkdown(finalMd);
      setShowMarkdown(true);
    } catch (e) {
      console.error("Failed to convert PDF to Markdown via API", e);
      if (!conversionCancelledRef.current) {
        setMarkdownError("Unable to convert this PDF to markdown.");
      }
    } finally {
      setMarkdownLoading(false);
    }
  };

  const fetchPdfAsArrayBuffer = async (url) => {
    const response = await fetch(url, { mode: "cors" });

    if (!response.ok) {
      throw new Error("Failed to download PDF");
    }

    return await response.arrayBuffer();
  };

  const extractPageNumbers = (input) => {
    const trimmed = input.trim();
    if (!trimmed) return [];

    const parts = trimmed.split(",");
    const pages = new Set();

    parts.forEach((part) => {
      const segment = part.trim();
      if (!segment) return;

      if (segment.includes("-")) {
        const [startStr, endStr] = segment.split("-").map((s) => s.trim());
        const start = Number(startStr);
        const end = Number(endStr);
        if (
          Number.isFinite(start) &&
          Number.isFinite(end) &&
          start > 0 &&
          end >= start
        ) {
          for (let p = start; p <= end; p += 1) {
            pages.add(p);
          }
        }
      } else {
        const num = Number(segment);
        if (Number.isFinite(num) && num > 0) {
          pages.add(num);
        }
      }
    });

    return Array.from(pages).sort((a, b) => a - b);
  };

  const handleExtractAndLog = async () => {
    setPagesError("");

    if (!selectedMaterial || !pagesInput.trim()) return;

    try {
      if (typeof onExtract === "function") {
        onExtract({
          materialId: selectedMaterial.id || selectedMaterial._id,
          pages: "",
          text: pagesInput.trim(),
        });
      }
    } catch (e) {
      console.error("Failed to save context text", e);
      setPagesError("Unable to save this context.");
    }
  };

  if (!open) return null;

  const renderMaterialIcon = (item) => {
    const type = (item?.materials_type || "").toLowerCase();
    if (type === "pdf") {
      return (
        <img
          src={pdfIcon}
          alt="PDF Icon"
          className="context-popup-material-icon-img"
        />
      );
    }
    if (type === "video" || type === "videos") {
      return (
        <img
          src={playIcon}
          alt="Video Icon"
          className="context-popup-material-icon-img"
        />
      );
    }
    if (type === "audio") {
      return (
        <img
          src={audioIcon}
          alt="Audio Icon"
          className="context-popup-material-icon-img"
        />
      );
    }
    if (type === "img" || type === "image" || type === "images") {
      return (
        <img
          src={imgIcon}
          alt="Image Icon"
          className="context-popup-material-icon-img"
        />
      );
    }
    if (type === "powerpoint" || type === "ppt" || type === "pptx") {
      return (
        <img
          src={powerpointIcon}
          alt="PowerPoint Icon"
          className="context-popup-material-icon-img"
        />
      );
    }
    return (
      <span className="context-popup-file-icon">
        <FileIcon />
      </span>
    );
  };

  return (
    <div className="common-popup-overlay high-z-index" onClick={onClose}>
      <div
        className="common-popup-container context-popup-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="popup-header with-close">
          {selectedMaterial ? (
            <button
              type="button"
              className="icon-button"
              onClick={() => {
                conversionCancelledRef.current = true;
                try {
                  if (conversionSocketRef.current)
                    conversionSocketRef.current.close();
                } catch (_) {}
                conversionSocketRef.current = null;

                setSelectedMaterial(null);
                setPagesInput("");
                setPagesError("");
                setNumPages(0);
                setMarkdown("");
                setMarkdownError("");
                setMarkdownLoading(false);
              }}
            >
              <ArrowBackIcon />
            </button>
          ) : (
            <span />
          )}
          <h2>Context Materials</h2>
          <button
            type="button"
            className="popup-close-button"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="popup-content">
          {loading && (
            <GlobalLoader visible={loading} text="Loading materials..." />
          )}
          {!loading && error && <p className="popup-error-message">{error}</p>}
          {!loading && !error && (
            <div className="question-bank-container context-popup-materials-wrapper">
              {selectedMaterial ? (
                <div className="context-popup-layout">
                  <div className="context-popup-preview">
                    <div className="context-popup-preview-header">
                      <div className="context-popup-preview-title-row">
                        {selectedMaterial.materials_type === "pdf" &&
                          showMarkdown &&
                          !markdownLoading && (
                            <button
                              type="button"
                              className="icon-button"
                              onClick={() => {
                                conversionCancelledRef.current = true;
                                try {
                                  if (conversionSocketRef.current)
                                    conversionSocketRef.current.close();
                                } catch (_) {}
                                conversionSocketRef.current = null;

                                setShowMarkdown(false);
                              }}
                              title="Back to PDF"
                            >
                              <ArrowBackIcon />
                            </button>
                          )}
                        <h3 className="context-popup-preview-title">
                          {selectedMaterial.name ||
                            selectedMaterial.title ||
                            selectedMaterial.id ||
                            "Selected Material"}
                        </h3>
                      </div>
                      {selectedMaterial.materials_type === "pdf" && (
                        <div className="context-popup-preview-actions">
                          {markdownTotalPages > 0 ? (
                            <span className="context-popup-generation-chip">
                              Content generation{" "}
                              {Math.min(
                                markdownCurrentPage,
                                markdownTotalPages,
                              )}
                              /{markdownTotalPages}
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="popup-submit-button"
                              onClick={handleConvertToMarkdown}
                              disabled={markdownLoading || isMarkdownConverted}
                            >
                              Convert to Markdown
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    {materialFetchLoading && (
                      <GlobalLoader
                        visible={materialFetchLoading}
                        text="Loading material..."
                      />
                    )}
                    {!materialFetchLoading && materialFetchError && (
                      <p className="popup-error-message">
                        {materialFetchError}
                      </p>
                    )}
                    {selectedMaterial.materials_type === "pdf" &&
                      selectedMaterial.content && (
                        <>
                          {markdownLoading && markdownCurrentPage === 0 && (
                            <div className="context-popup-generating">
                              Generating...
                            </div>
                          )}

                          {showMarkdown &&
                            Object.keys(generatedMarkdownPages).length > 0 &&
                            !markdownError && (
                              <div className="context-popup-markdown-preview">
                                {Object.keys(generatedMarkdownPages)
                                  .map((k) => Number(k))
                                  .filter((n) => Number.isFinite(n))
                                  .sort((a, b) => a - b)
                                  .map((pageNo) => {
                                    const pageMd =
                                      generatedMarkdownPages[pageNo] || "";
                                    if (!pageMd) return null;
                                    const isChecked = Boolean(
                                      selectedGeneratedPages[pageNo],
                                    );
                                    return (
                                      <div
                                        key={`gen-page-${pageNo}`}
                                        className="context-popup-generated-page"
                                      >
                                        <div className="context-popup-generated-page-header">
                                          <div className="context-popup-generated-page-title">
                                            Page {pageNo}
                                          </div>
                                          <label className="context-popup-generated-page-checkbox">
                                            <input
                                              type="checkbox"
                                              checked={isChecked}
                                              onChange={(e) => {
                                                const checked =
                                                  e.target.checked;
                                                setSelectedGeneratedPages(
                                                  (prev) => ({
                                                    ...prev,
                                                    [pageNo]: checked,
                                                  }),
                                                );
                                              }}
                                            />
                                            Use in context
                                          </label>
                                        </div>
                                        <MarkdownViewer content={pageMd} />
                                      </div>
                                    );
                                  })}
                              </div>
                            )}

                          {!markdownLoading &&
                            !showMarkdown &&
                            !markdownError && (
                              <iframe
                                src={selectedMaterial.content}
                                title="Selected material preview"
                                className="context-popup-preview-iframe"
                              />
                            )}

                          {!markdownLoading && markdownError && (
                            <p className="popup-error-message context-popup-markdown-error">
                              {markdownError}
                            </p>
                          )}
                        </>
                      )}
                    {!selectedMaterial.content && (
                      <p className="context-popup-no-preview">
                        No preview available for this material.
                      </p>
                    )}
                  </div>
                  <div className="context-popup-pages-panel">
                    <div
                      className="context-popup-pages-label"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 10,
                      }}
                    >
                      <span>Context</span>
                      {String(appliedPagesText).trim() && (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "6px 10px",
                            borderRadius: 999,
                            background: "rgba(99, 102, 241, 0.12)",
                            border: "1px solid rgba(99, 102, 241, 0.25)",
                            color: "rgba(55, 65, 81, 1)",
                            fontSize: 12,
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Applied - {appliedPagesText}
                        </span>
                      )}
                    </div>

                    <div style={{ marginBottom: 10 }}>
                      <label>Pages</label>
                      <div
                        style={{
                          display: "flex",
                          gap: 10,
                          alignItems: "center",
                        }}
                      >
                        <input
                          type="text"
                          className="popup-input"
                          placeholder="e.g. 1-8, 1,2,3, 1,3,4-8"
                          value={contextPagesSpecDraft}
                          onChange={(e) => {
                            const nextValue = e.target.value;

                            if (!/^[0-9,\-\s]*$/.test(String(nextValue))) {
                              setPagesError(
                                "Only numbers, comma (,) and hyphen (-) are allowed.",
                              );
                              return;
                            }

                            const trimmed = String(nextValue).trim();
                            const last = trimmed
                              ? trimmed[trimmed.length - 1]
                              : "";
                            const shouldValidateNow = last && /\d/.test(last);
                            if (shouldValidateNow) {
                              const msg = validatePagesSpec(nextValue);
                              if (msg) {
                                setPagesError(msg);
                                return;
                              }
                            }

                            setPagesError("");
                            pagesSpecManuallyEditedRef.current = true;
                            setContextPagesSpecDraft(nextValue);
                          }}
                        />
                        <button
                          type="button"
                          className="popup-submit-button"
                          style={{ width: "auto", padding: "10px 14px" }}
                          onClick={() => {
                            setPagesError("");

                            const spec = String(
                              contextPagesSpecDraft || "",
                            ).trim();
                            if (!spec) return;

                            const msg = validatePagesSpec(spec);
                            if (msg) {
                              setPagesError(msg);
                              return;
                            }

                            pagesSpecManuallyEditedRef.current = false;
                            setContextPagesSpec(spec);
                          }}
                          disabled={
                            !String(contextPagesSpecDraft || "").trim() ||
                            Object.keys(generatedMarkdownPages).length === 0
                          }
                        >
                          Apply
                        </button>
                        <button
                          type="button"
                          className="popup-submit-button"
                          style={{ width: "auto", padding: "10px 14px" }}
                          onClick={() => {
                            setContextPagesSpecDraft("");
                            setContextPagesSpec("");
                            setAppliedPagesText("");
                            setPagesInput("");
                            setSelectedGeneratedPages({});
                            pagesSpecManuallyEditedRef.current = false;
                          }}
                          disabled={
                            !String(
                              contextPagesSpec || contextPagesSpecDraft || "",
                            ).trim() ||
                            Object.keys(generatedMarkdownPages).length === 0
                          }
                        >
                          Clear
                        </button>
                      </div>

                      {pagesError && (
                        <p className="popup-error-message context-popup-pages-error">
                          {pagesError}
                        </p>
                      )}
                    </div>

                    <textarea
                      className="popup-input"
                      placeholder="Paste or type context here..."
                      rows={14}
                      value={pagesInput}
                      onChange={(e) => setPagesInput(e.target.value)}
                    />
                    {/* 
                    {numPages > 0 && selectedMaterial?.materials_type === "pdf" && (
                      <p className="context-popup-pages-hint">
                        This PDF has {numPages} page(s).
                      </p>
                    )} */}

                    <button
                      type="button"
                      className="popup-submit-button"
                      disabled={!pagesInput.trim()}
                      onClick={handleExtractAndLog}
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <div className="qb-card context-popup-materials-card">
                  <div className="folder-grid">
                    {materials.length === 0 ? (
                      <p className="context-popup-no-materials">
                        No materials found.
                      </p>
                    ) : (
                      materials.map((m) => {
                        const id = m.id || m._id;
                        return (
                          <div
                            key={id}
                            className="folder-item file-item context-popup-material-item"
                            onClick={() => {
                              handleSelectMaterial(m);
                            }}
                          >
                            <div className="folder-icon">
                              {renderMaterialIcon(m)}
                            </div>
                            <div className="folder-name">
                              <TooltipText
                                text={m.name || m.title || id || "Untitled"}
                                fullWidth
                                placement="top"
                                always
                              />
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContextPopup;
